/**
 * Recursively searches through menu items to find a menu item matching the given URL
 * @param {Array} menuItems - Array of menu items to search through
 * @param {string} url - URL to match (without leading slash)
 * @returns {Object|null} - Matching menu item or null if not found
 */
export const findMenuItemByUrl = (menuItems, url) => {
  if (!menuItems || !Array.isArray(menuItems) || !url) {
    return null;
  }

  // Remove leading slash if present
  const cleanUrl = url.startsWith('/') ? url.slice(1) : url;

  // Remove query parameters for matching
  const urlWithoutQuery = cleanUrl.split('?')[0];

  for (const item of menuItems) {
    // Check if this item's URL matches
    if (item.url) {
      const itemUrl = item.url.startsWith('/') ? item.url.slice(1) : item.url;
      const itemUrlWithoutQuery = itemUrl.split('?')[0];

      // Exact match or pathname includes the item URL
      if (
        urlWithoutQuery === itemUrlWithoutQuery ||
        urlWithoutQuery.startsWith(itemUrlWithoutQuery + '/')
      ) {
        return {
          ...item,
          icon: undefined,
          children: undefined,
          submenu: undefined,
        };
      }
    }

    // Recursively search in submenu
    if (item.submenu && Array.isArray(item.submenu)) {
      const found = findMenuItemByUrl(item.submenu, url);
      if (found) {
        return found;
      }
    }

    // Recursively search in children
    if (item.children && Array.isArray(item.children)) {
      const found = findMenuItemByUrl(item.children, url);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

/**
 * Creates a temporary menu item from a URL when no match is found in menu config
 * @param {string} url - URL to create menu item for
 * @returns {Object} - Temporary menu item
 */
export const createTempMenuItemFromUrl = (url) => {
  const cleanUrl = url.startsWith('/') ? url.slice(1) : url;
  const urlWithoutQuery = cleanUrl.split('?')[0];

  // Extract a readable name from the URL
  const name = urlWithoutQuery
    .split('/')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

  return {
    name: urlWithoutQuery.replace(/\//g, '.').replace(/-/g, '.'),
    url: urlWithoutQuery,
    id: `temp_${urlWithoutQuery.replace(/\//g, '_')}`,
    data: {},
  };
};

/**
 * Finds or creates a menu item for the given URL
 * @param {string} url - URL to find/create menu item for
 * @param {Array} availableRoutes - Routes from config
 * @param {Array} existingMenuItems - Current menu items
 * @returns {Object} - Menu item to use
 */
export const findOrCreateMenuItem = (
  url,
  availableRoutes,
  existingMenuItems,
) => {
  // 1. Try config routes
  let menuItem = findMenuItemByUrl(availableRoutes, url);

  // 2. Try existing menu items (exact match only)
  if (!menuItem) {
    menuItem = existingMenuItems.find((item) => item.url === url);
  }

  // 3. Create temp item if not found
  if (!menuItem) {
    menuItem = createTempMenuItemFromUrl(url);
  }

  return menuItem;
};
